import DOMPurify from "dompurify";
import { istLeererBlocktext } from "./blockModell";
import { istBildBreite } from "./bildBreite";

/**
 * HTML-Hygiene der Notizblöcke.
 *
 * ══ DER CLIENT IST DIE EINZIGE LEITPLANKE ══════════════════════════════════
 * Der Server sanitisiert Notiz-HTML NICHT (`vorgang-note.service.ts` schreibt
 * `input.text` unverändert; `sanitize-html` läuft im Backend nur für Mails
 * und Termin-Notizen). Bis 14.09.2026 behauptete dieser Kopf das Gegenteil.
 * Deshalb läuft die Reinigung hier ZWEIMAL — beim Speichern und unmittelbar
 * vor dem Einsetzen ins contentEditable — und sie muss lückenlos bleiben:
 * wer die API direkt anspricht, kann beliebiges Markup ablegen, und gesehen
 * wird nur, was hier durchkommt. Ein serverseitiges Sanitizing ist ein
 * eigener Folge-PR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Seit PO 14.09.2026 („bilder in die notiz laden, links wie in Notion") sind
 * `<img>` und `<a>` erlaubt — mit eng gefassten Quellen, siehe
 * {@link nachreinigen}.
 */

/** Erlaubte Auszeichnung eines Blocks — Text, Listen, Einzug, Bild, Link. */
const ERLAUBTE_TAGS = [
    "b", "strong", "i", "em", "u", "s", "mark",
    "br", "div", "p", "span",
    "ul", "ol", "li", "blockquote",
    "img", "a",
];

const ERLAUBTE_ATTRIBUTE = [
    "class", "data-ref-art", "data-ref-id",
    // Markierte Stellen (PO 18.09.2026): `data-anker` verbindet die Stelle mit
    // ihrem Kommentarfaden, `data-von`/`data-am` halten fest, wer eine Stelle
    // wann abgehakt hat. Alle drei werden in `nachreinigen` geprüft — sie
    // landen im Browser in Attributselektoren und Titeltexten.
    "data-anker", "data-von", "data-am",
    // Bild: eigenes Notizbild (`data-bild-id`, Quelle wird beim Anzeigen
    // nachgeladen) oder externer Verweis (`src`, nur https).
    "src", "alt", "width", "height", "data-bild-id", "loading", "referrerpolicy",
    // Link. `target` und `rel` werden unten ohnehin erzwungen.
    "href", "target", "rel",
];

/**
 * Erlaubte Bildquelle: eine https-URL (Notion-Stil „Embed link") oder der
 * kanonische Download-Pfad eines Dokuments. KEIN `data:` — DOMPurify ließe
 * base64 auf `<img>` per Default durch, und genau das soll nie in die
 * Datenbank (siehe `bildDarstellung.ts`). KEIN `blob:` — das ist die
 * flüchtige Anzeige-URL einer Sitzung. KEIN `http:` — gemischter Inhalt.
 */
/** Quellen, die ein fremdes `<img>` behalten darf (sonst verwirft der Reiniger es). */
export const BILD_SRC = /^(?:https:\/\/\S+|\/api\/documents\/\d+\/download)$/i;

/**
 * Der Schlüssel einer markierten Stelle. Dieselbe Grenze zieht das Backend
 * (`sauberterAnker`); stünde sie nur dort, käme über einen anderen Weg
 * beliebiger Text in einen Attributselektor.
 */
const ANKER = /^[A-Za-z0-9]{6,24}$/;
/** Wer abgehakt hat — ein Benutzerschlüssel, keine Sonderzeichen. */
const ABHAKER = /^[A-Za-z0-9._@-]{1,255}$/;
/** Wann abgehakt wurde — ein ISO-Zeitpunkt, nichts anderes. */
const ISO_ZEIT = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z?$/;

/** Erlaubtes Linkziel: Web oder Mailadresse. `javascript:` fängt DOMPurify vorher. */
const LINK_HREF = /^(?:https?:\/\/\S+|mailto:\S+)$/i;

/**
 * Was DOMPurify nicht weiß: WELCHE Quellen ein Bild und WELCHE Ziele ein Link
 * haben darf, und dass jeder Link in einem neuen Tab ohne Opener aufgeht.
 *
 *  · `img[data-bild-id]` — ein eigenes Notizbild. Die Id muss eine Zahl sein;
 *    ein `src` wird VERWORFEN (beim Anzeigen kommt die `blob:`-URL neu, und
 *    gespeichert wird nur der Marker).
 *  · sonstiges `img` — nur mit erlaubtem `src`, sonst fliegt das Element.
 *    Externe Bilder tragen `referrerpolicy=no-referrer`, damit die Notiz
 *    keinem fremden Host verrät, woher sie kommt.
 *  · `a` — nur mit erlaubtem `href`; sonst bleibt der Linktext als Text
 *    stehen (das Element wird aufgelöst). `target=_blank` und
 *    `rel=noopener noreferrer` werden immer gesetzt — nicht nur beim
 *    Einfügen, sonst hinge die Sicherheit an dem Weg, auf dem der Link
 *    hereinkam.
 */
function nachreinigen(html: string): string {
    // Der Schnellausstieg spart den DOM-Umweg für gewöhnlichen Text. Er muss
    // ALLES nennen, was unten geprüft wird — sonst käme ein `data-anker` an
    // einem `<p>` ungeprüft durch, weil dort kein `span` steht.
    if (!/<(?:img|a|span)\b|data-(?:anker|von|am)=/i.test(html)) return html;
    const topf = document.createElement("div");
    topf.innerHTML = html;

    /*
     * MARKIERTE STELLEN. Die drei Attribute gehören AUSSCHLIESSLICH an einen
     * `span` mit der passenden Klasse — sonst stünde `data-anker` irgendwo im
     * Text und führte einen Faden an eine Stelle, die keine ist. Was die
     * Prüfung nicht besteht, verliert das ATTRIBUT, nicht den Text: Eine
     * Markierung ist Auszeichnung, ihr Verlust darf keinen Satz kosten.
     */
    for (const el of Array.from(topf.querySelectorAll("[data-anker],[data-von],[data-am]"))) {
        const anmerk = el.tagName === "SPAN" && el.classList.contains("nb-anmerk");
        const getan = el.tagName === "SPAN" && el.classList.contains("nb-getan");
        const anker = el.getAttribute("data-anker");
        if (!anmerk || !anker || !ANKER.test(anker)) el.removeAttribute("data-anker");
        const von = el.getAttribute("data-von");
        if (!getan || !von || !ABHAKER.test(von)) el.removeAttribute("data-von");
        const am = el.getAttribute("data-am");
        if (!getan || !am || !ISO_ZEIT.test(am)) el.removeAttribute("data-am");
    }

    for (const img of Array.from(topf.querySelectorAll("img"))) {
        const bildId = img.getAttribute("data-bild-id");
        if (bildId !== null) {
            if (!/^\d+$/.test(bildId)) {
                img.remove();
                continue;
            }
            img.removeAttribute("src");
            img.removeAttribute("loading");
            img.removeAttribute("referrerpolicy");
            // Die gewählte Größe (User 23.10.2026) — nur eine Breite in
            // Pixeln; die Höhe folgt dem Seitenverhältnis.
            if (!istBildBreite(img.getAttribute("width"))) img.removeAttribute("width");
            img.removeAttribute("height");
            continue;
        }
        const src = (img.getAttribute("src") ?? "").trim();
        if (!BILD_SRC.test(src)) {
            img.remove();
            continue;
        }
        if (/^https:/i.test(src)) {
            img.setAttribute("referrerpolicy", "no-referrer");
            img.setAttribute("loading", "lazy");
        }
    }

    for (const a of Array.from(topf.querySelectorAll("a"))) {
        const href = (a.getAttribute("href") ?? "").trim();
        if (!LINK_HREF.test(href)) {
            a.replaceWith(...Array.from(a.childNodes));
            continue;
        }
        a.setAttribute("href", href);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
    }

    return topf.innerHTML;
}

export function reinigeBlockHtml(html: string): string {
    const sauber = DOMPurify.sanitize(html ?? "", {
        ALLOWED_TAGS: ERLAUBTE_TAGS,
        ALLOWED_ATTR: ERLAUBTE_ATTRIBUTE,
        // DOMPurify ließe sonst JEDES `data-*` durch, an der Liste oben
        // vorbei — die drei, die gebraucht werden, stehen dort ausdrücklich.
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "style"],
        FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction", "style"],
    });
    return nachreinigen(sauber);
}

/**
 * Ist der Block inhaltsleer? Ein contentEditable hinterlässt beim Leeren je
 * nach Browser `<br>` oder `<div><br></div>`; ohne diese Prüfung entstünde bei
 * jedem Hineinklicken ein leerer Block.
 *
 * Die Regel selbst steht in `blockModell.ts` — dieses Modul bringt DOMPurify
 * mit und braucht ein DOM, die Prüfung läuft aber auch in reinen Ableitungen
 * ohne DOM (`bloeckeAus`, `archivZeilen`). Zwei Fassungen liefen auseinander,
 * und „leer" entschiede dann je nach Aufrufer anders.
 */
export function istLeer(html: string): boolean {
    return istLeererBlocktext(html);
}
